import React, { useState } from "react";
import axiosClient from "../api/axiosClient";
import { uploadProductImage } from "../api/imageService";

import "./AdminProductForm.css";

const AdminProductForm = ({ product, onSuccess, onCancel }) => {
  const isEditing = product !== null;

  const [name, setName] = useState(product ? product.name : "");

  const [description, setDescription] = useState(
    product ? product.description : "",
  );

  const [price, setPrice] = useState(product ? product.price : "");

  const [salePrice, setSalePrice] = useState(
    product ? (product.salePrice ?? "") : "",
  );

  const [category, setCategory] = useState(product ? product.category : "");

  const [imageUrl, setImageUrl] = useState(product ? product.imageUrl : "");

  const [stock, setStock] = useState(product ? product.stock : "");

  const [selectedImage, setSelectedImage] = useState(null);

  const [imagePreview, setImagePreview] = useState(
    product ? product.imageUrl : "",
  );

  const [loading, setLoading] = useState(false);

  const [uploadingImage, setUploadingImage] = useState(false);

  const [error, setError] = useState("");

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      setError("Upload a JPG, PNG or WEBP image. Maximum size: 10 MB.");

      event.target.value = "";

      return;
    }

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      setError("Image size must be less than 10 MB.");

      event.target.value = "";

      return;
    }

    setError("");

    setSelectedImage(file);

    const previewUrl = URL.createObjectURL(file);

    setImagePreview(previewUrl);
  };

  const handleImageUpload = async () => {
    if (!selectedImage) {
      setError("Please choose an image first.");

      return;
    }

    try {
      setError("");

      setUploadingImage(true);

      const uploadedImageUrl = await uploadProductImage(selectedImage);

      setImageUrl(uploadedImageUrl);

      setImagePreview(uploadedImageUrl);

      setSelectedImage(null);
    } catch (error) {
      console.error("Image upload error:", error);

      setError("Unable to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");

    /* BASIC VALIDATION */

    if (!name.trim()) {
      setError("Please enter a product name.");

      return;
    }

    if (!category.trim()) {
      setError("Please enter a product category.");

      return;
    }

    if (price === "" || Number(price) <= 0) {
      setError("Please enter a valid product price.");

      return;
    }

    if (salePrice !== "" && Number(salePrice) >= Number(price)) {
      setError("Sale price must be lower than the original price.");

      return;
    }

    if (stock === "" || Number(stock) < 0) {
      setError("Stock cannot be negative.");

      return;
    }

    if (!imageUrl) {
      setError("Please upload a product image.");

      return;
    }

    try {
      setLoading(true);

      const productData = {
        name: name.trim(),

        description: description.trim(),

        price: Number(price),

        salePrice: salePrice === "" ? null : Number(salePrice),

        category: category.trim(),

        imageUrl: imageUrl,

        stock: Number(stock),
      };

      if (isEditing) {
        await axiosClient.put(`/admin/products/${product.id}`, productData);
      } else {
        await axiosClient.post("/admin/products", productData);
      }

      onSuccess();
    } catch (error) {
      console.error("Product save error:", error);

      setError("Unable to save product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-product-form-wrapper">
      {/* FORM HEADER */}

      <div className="admin-product-form-header">
        <div>
          <p className="admin-form-eyebrow">
            {isEditing ? "PRODUCT EDITOR" : "NEW PRODUCT"}
          </p>

          <h2>{isEditing ? "Edit Product" : "Add New Product"}</h2>

          <p>Add the product information shown to your customers.</p>
        </div>

        <button type="button" className="admin-form-close" onClick={onCancel}>
          ×
        </button>
      </div>

      <form className="admin-product-form" onSubmit={handleSubmit}>
        {/* BASIC INFORMATION */}

        <div className="admin-form-section">
          <div className="admin-form-section-title">Product Information</div>

          <div className="admin-form-grid">
            <div className="admin-form-field">
              <label>Product Name</label>

              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Example: Designer Necklace"
              />
            </div>

            <div className="admin-form-field">
              <label>Category</label>

              <input
                type="text"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                placeholder="Example: Necklace"
              />
            </div>

            <div className="admin-form-field">
              <label>Price</label>

              <div className="admin-price-input">
                <span>₹</span>

                <input
                  type="number"
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  min="1"
                  step="0.01"
                  placeholder="1299"
                />
              </div>
            </div>

            <div className="admin-form-field">
              <label>
                Sale Price
                <small> Optional</small>
              </label>

              <div className="admin-price-input">
                <span>₹</span>

                <input
                  type="number"
                  value={salePrice}
                  onChange={(event) => setSalePrice(event.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="999"
                />
              </div>
            </div>

            <div className="admin-form-field">
              <label>Stock Quantity</label>

              <input
                type="number"
                value={stock}
                onChange={(event) => setStock(event.target.value)}
                min="0"
                step="1"
                placeholder="10"
              />
            </div>
          </div>

          <div className="admin-form-field admin-description-field">
            <label>Description</label>

            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows="5"
              placeholder="Describe the jewellery piece..."
            />
          </div>
        </div>

        {/* IMAGE */}

        <div className="admin-form-section">
          <div className="admin-form-section-title">Product Image</div>

          <div className="admin-image-upload-area">
            <div className="admin-image-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Product preview" />
              ) : (
                <div className="admin-image-placeholder">
                  <span>◇</span>

                  <p>Product image</p>
                </div>
              )}
            </div>

            <div className="admin-image-controls">
              <p className="admin-image-help">
                Upload a JPG, PNG or WEBP image. Maximum size: 10 MB.
              </p>

              <input
                id="productImage"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleImageChange}
              />

              {selectedImage && (
                <button
                  type="button"
                  className="admin-upload-button"
                  onClick={handleImageUpload}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? "Uploading..." : "Upload Image"}
                </button>
              )}

              {imageUrl && !selectedImage && (
                <p className="admin-upload-success">✓ Image ready</p>
              )}
            </div>
          </div>
        </div>

        {/* ERROR */}

        {error && <div className="admin-form-error">{error}</div>}

        {/* ACTIONS */}

        <div className="admin-form-actions">
          <button
            type="button"
            className="admin-form-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="admin-form-submit"
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : isEditing
                ? "Update Product"
                : "Add Product"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;
